import { BigNumber } from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import SimpleTooltip from '~/components/data-display/SimpleTooltip';
import { TableCell, TableRow } from '~/components/data-display/Table/Table';
import { HStack } from '~/components/layout/Stack';
import LabelWithTooltip from '~/components/table/LabelWithTooltip';
import { MainLabel } from '~/components/table/TableLabels';
import IconDelete from '~/icons/icon-delete.svg?react';
import IconEdit from '~/icons/icon-edit.svg?react';
import { CounterpartKind } from '~/types/counterparts';
import { DocumentType } from '~/types/document';
import { BookkeepingAdjustmentType, BookkeepingTransaction } from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { RtDeviceSetting } from '~/types/rt-device-settings';
import { isDefined } from '~/types/typeGuards';
import { VATReportingPeriodStatus } from '~/types/vat-bookkeeping';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { isNotEmpty } from '~/utils/helpers';
import { absMoney, formatMoney, sumMoney } from '~/utils/money';
import styles from './GeneralBookkeepingCashBookTable.module.scss';
import GeneralBookkeepingCashBookTooltip from './GeneralBookkeepingCashBookTooltip';

interface GeneralBookkeepingCashBookTableRowProps {
  deviceSettingsMap: Map<string, RtDeviceSetting>;
  transaction: BookkeepingTransaction;
  onEdit: (transaction: BookkeepingTransaction) => void;
  onDelete: (transaction: BookkeepingTransaction) => void;
}

const isAmortizationTransaction = (transaction: BookkeepingTransaction): boolean =>
  transaction.bookkeepingEntries.some(
    (entry) => entry.bookkeepingAdjustment?.adjustmentType === BookkeepingAdjustmentType.AMMORTAMENTO,
  );

const EditButton: React.FC<{
  transaction: BookkeepingTransaction;
  onEdit: (transaction: BookkeepingTransaction) => void;
}> = ({ transaction, onEdit }) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'registrations.delete_vat_registration' });

  if (transaction.adjustmentId) {
    const tooltipTitle = isAmortizationTransaction(transaction)
      ? t('amortization_transaction_tooltip')
      : t('adjustment_transaction_tooltip');
    return (
      <SimpleTooltip title={tooltipTitle} mode="div">
        <IconButton disabled variant="ghost" size="small" Icon={IconEdit} onClick={() => onEdit(transaction)} />
      </SimpleTooltip>
    );
  }
  return <IconButton variant="ghost" size="small" Icon={IconEdit} onClick={() => onEdit(transaction)} />;
};

const DeleteButton = ({
  transaction,
  onDelete,
}: {
  transaction: BookkeepingTransaction;
  onDelete: (transaction: BookkeepingTransaction) => void;
}) => {
  const { t: tDeleteVatRegistration } = useTranslation('bookkeeping', {
    keyPrefix: 'registrations.delete_vat_registration',
  });

  if (transaction.adjustmentId) {
    const tooltipTitle = isAmortizationTransaction(transaction)
      ? tDeleteVatRegistration('amortization_transaction_tooltip')
      : tDeleteVatRegistration('adjustment_transaction_tooltip');
    return (
      <SimpleTooltip title={tooltipTitle} mode="div">
        <IconButton disabled variant="ghost" size="small" Icon={IconDelete} onClick={() => onDelete(transaction)} />
      </SimpleTooltip>
    );
  }
  if (
    isDefined(transaction.vatRegistration) &&
    transaction.vatRegistration.vatReportingPeriod.status === VATReportingPeriodStatus.CLOSED
  ) {
    return (
      <SimpleTooltip title={tDeleteVatRegistration('closed_vat_registration_tooltip')} mode="div">
        <IconButton disabled variant="ghost" size="small" Icon={IconDelete} onClick={() => onDelete(transaction)} />
      </SimpleTooltip>
    );
  }
  return <IconButton variant="ghost" size="small" Icon={IconDelete} onClick={() => onDelete(transaction)} />;
};

const extractAmount = (transaction: BookkeepingTransaction): Money | null => {
  const positive: Money[] = [];

  for (const entry of transaction.bookkeepingEntries) {
    if (entry.document) {
      return entry.document.grossAmount;
    }

    const amount = entry.amount;
    const value = new BigNumber(amount.amount);

    if (value.isGreaterThan(0)) {
      positive.push(amount);
    }
  }

  if (positive.length) {
    return sumMoney(...positive);
  }

  return null;
};

const extractCounterpart = (transaction: BookkeepingTransaction): string | null => {
  if (isNotEmpty(transaction.transactions)) {
    return transaction.transactions.find((t) => isDefined(t.counterpartName))?.counterpartName ?? null;
  }

  return transaction.bookkeepingEntries[0]?.counterpart?.companyName ?? null;
};

const buildDescription = (
  transaction: BookkeepingTransaction,
  deviceSettingsMap: Map<string, RtDeviceSetting> | undefined,
) => {
  const registrationDocument = transaction.vatRegistration?.documents[0];
  if (
    registrationDocument?.documentType === DocumentType.Bill &&
    registrationDocument.counterpart?.kind === CounterpartKind.Virtual &&
    deviceSettingsMap?.get(registrationDocument.counterpart.id)?.description
  ) {
    const deviceSettings = deviceSettingsMap.get(registrationDocument.counterpart.id);
    return `${registrationDocument.counterpart.taxNumber} - ${deviceSettings?.description}`;
  }
  return transaction.description;
};

const GeneralBookkeepingCashBookTableRow: React.FC<GeneralBookkeepingCashBookTableRowProps> = ({
  deviceSettingsMap,
  transaction,
  onDelete,
  onEdit,
}) => {
  const document = transaction.bookkeepingEntries.find((entry) => entry.document)?.document;
  const amount = extractAmount(transaction);
  const counterpart = extractCounterpart(transaction);
  const description = buildDescription(transaction, deviceSettingsMap);

  return (
    <TableRow id={transaction.id}>
      <TableCell>
        <MainLabel className={styles.secondaryLabel} tabularNums>
          {formatFromISOToDisplay(transaction.date)}
        </MainLabel>
      </TableCell>
      <TableCell>
        <LabelWithTooltip className={styles.secondaryLabel} label={transaction.number ? transaction.number : '-'} />
      </TableCell>
      <TableCell>
        <div className={styles.labelWrapper}>
          <LabelWithTooltip tooltipClassName={styles.tooltip} label={description} />
        </div>
      </TableCell>
      <TableCell>
        <div className={styles.labelWrapper}>
          <LabelWithTooltip className={styles.secondaryLabel} label={document?.number ?? '-'} />
        </div>
      </TableCell>
      <TableCell>
        <MainLabel className={styles.secondaryLabel} tabularNums>
          {document?.creationDate ? formatFromISOToDisplay(document.creationDate) : '-'}
        </MainLabel>
      </TableCell>
      <TableCell>
        <div className={styles.labelWrapper}>
          <LabelWithTooltip
            className={styles.secondaryLabel}
            tooltipClassName={styles.tooltip}
            label={counterpart ?? '-'}
          />
        </div>
      </TableCell>
      <TableCell align="right">
        <MainLabel className={styles.secondaryLabel} align="right" tabularNums>
          {amount ? formatMoney(absMoney(amount)) : '-'}
        </MainLabel>
      </TableCell>
      <TableCell>
        <GeneralBookkeepingCashBookTooltip bookkeepingTransaction={transaction} />
      </TableCell>
      <TableCell align="right">
        <HStack gap={1}>
          <EditButton transaction={transaction} onEdit={onEdit} />
          <DeleteButton transaction={transaction} onDelete={onDelete} />
        </HStack>
      </TableCell>
    </TableRow>
  );
};

export default GeneralBookkeepingCashBookTableRow;
